import { useRef, useState } from 'react';
import { enums } from '@kattana-io/common';

const countdownTime = 5;
const circleRadius = 15.9;
const circleCircumference = 2 * Math.PI * circleRadius;

interface IUnfavouriteStarProps {
  tag: enums.Tag;
  onUnfav(): void;
}

const UnfavouriteStar = ({ tag, onUnfav }: IUnfavouriteStarProps) => {
  const [isDeleting, setIsDeleting] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const [remainingTime, setRemainingTime] = useState(0);
  const startTimeRef = useRef<number | null>(null);
  const pauseTimeRef = useRef<number | null>(null);
  const rafRef = useRef<number | null>(null);
  const circleRef = useRef<SVGCircleElement>(null);

  const onHoverStart = () => {
    setIsHovered(true);
    if (rafRef.current) {
      cancelAnimationFrame(rafRef.current);
      pauseTimeRef.current = performance.now();
    }
  };

  const onHoverEnd = () => {
    setIsHovered(false);
    if (isDeleting) {
      if (pauseTimeRef.current && startTimeRef.current) {
        startTimeRef.current += performance.now() - pauseTimeRef.current;
        pauseTimeRef.current = null;
      }
      animate();
    }
  };

  const onDeleteCancel = (e: any) => {
    e.stopPropagation();
    e.preventDefault();
    setIsDeleting(false);
    setRemainingTime(0);
    startTimeRef.current = null;
    pauseTimeRef.current = null;
  };

  const animate = () => {
    const now = performance.now();
    if (startTimeRef.current === null) {
      startTimeRef.current = now;
    }
    const elapsed = (now - startTimeRef.current) / 1000;
    const remaining = countdownTime - elapsed;
    if (remaining <= 0 && isDeleting) {
      if (circleRef.current) {
        circleRef.current.setAttribute('stroke-dashoffset', '0');
      }
      onUnfav();
    } else {
      const offset =
        ((countdownTime - remaining) / countdownTime) * circleCircumference;
      if (circleRef.current) {
        circleRef.current.setAttribute('stroke-dashoffset', `${offset}`);
      }
      setRemainingTime(Math.ceil(remaining));
      rafRef.current = requestAnimationFrame(animate);
    }
  };

  const onDeleteInit = (e: any) => {
    e.stopPropagation();
    e.preventDefault();
    setIsDeleting(true);
    animate();
  };

  if (!isDeleting) {
    return (
      <div
        className={`unfavourite icon-star-active tag--${tag}`}
        data-qa={'unfavourite'}
        onClick={onDeleteInit}
      />
    );
  }

  return (
    <div className='unfavourite__timer_wrapper'>
      <div
        className={`unfavourite__timer ${
          isDeleting ? 'unfavourite__timer--deleting' : ''
        }`}
        onMouseEnter={isDeleting ? onHoverStart : undefined}
        onMouseLeave={isDeleting ? onHoverEnd : undefined}
      >
        <svg viewBox='0 0 50 50' cursor='pointer' onClick={onDeleteCancel}>
          <circle
            className='unfavourite__timer__circle'
            r={circleRadius}
            cx='25'
            cy='25'
            strokeDasharray={circleCircumference}
            strokeDashoffset={0}
            transform='rotate(-90 25 25)'
            ref={circleRef}
          />
          {isHovered ? (
            <path
              d='M20 20 30 30 M20 30 30 20'
              style={{
                fill: 'none',
                stroke: 'var(--grey-200)',
                strokeWidth: '2',
                strokeLinecap: 'round',
                strokeLinejoin: 'round',
                pointerEvents: 'none',
              }}
            />
          ) : (
            <text
              x='50%'
              y='50%'
              textAnchor='middle'
              fill='var(--grey-200)'
              dy='5.5px'
            >
              {remainingTime}
            </text>
          )}
        </svg>
      </div>
    </div>
  );
};

export default UnfavouriteStar;
